<template>
    <q-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" position="right"
        maximized transition-show="slide-left" transition-hide="slide-right">
        <q-card class="cart-menu-card">
            <!-- Header moderno con gradiente -->
            <q-toolbar class="bg-gradient-primary text-white">
                <q-icon name="shopping_cart" size="28px" class="q-mr-sm" />
                <q-toolbar-title>
                    <div class="text-h6">{{ cartTitle }}</div>
                    <div class="text-caption text-white-7">
                        {{ cartStore.items.length }} {{ cartStore.items.length === 1 ? 'producto' : 'productos' }} en el
                        carrito
                    </div>
                </q-toolbar-title>

                <!-- Badge de items -->
                <q-badge color="white" text-color="primary" :label="cartStore.items.length" class="q-mr-md"
                    v-if="cartStore.items.length > 0">
                    <q-icon name="inventory" size="16px" class="q-ml-xs" />
                </q-badge>

                <!-- Botón modo edición -->
                <q-btn v-if="cartStore.getMode === 'edit'" flat dense icon="edit_note" @click="cartStore.setMode('add')"
                    class="q-mr-sm">
                    <q-tooltip>Cambiar a Modo Adición</q-tooltip>
                </q-btn>

                <q-btn dense flat icon="close" @click="cartStore.toggle()">
                    <q-tooltip>Cerrar</q-tooltip>
                </q-btn>
            </q-toolbar>

            <!-- Progress bar del stepper -->
            <q-linear-progress :value="step / 3" color="primary" size="6px" class="progress-bar-animated" />

            <q-card-section class="cart-content">
                <q-stepper flat v-model="step" ref="stepper" color="primary" animated vertical class="modern-stepper">
                    <q-step :name="1" title="Productos del Carrito" caption="Revisa y edita los productos"
                        icon="shopping_basket" :done="step > 1" :header-nav="step > 1">
                        <StepShoppingCart />
                    </q-step>

                    <q-step :name="2" title="Datos de la Obra" caption="Selecciona o crea una obra" icon="business"
                        :done="step > 2" :header-nav="step > 2">
                        <StepObras />
                    </q-step>

                    <q-step :name="3" title="Datos del Presupuesto" caption="Completa la información del presupuesto"
                        icon="description" :done="step > 3" :header-nav="step > 3">
                        <StepPresupuesto />
                    </q-step>

                    <template v-slot:navigation>
                        <q-stepper-navigation class="modern-navigation">
                            <q-separator class="q-mb-md" />
                            <div class="row q-gutter-sm">
                                <div class="col">
                                    <q-btn v-if="step > 1" outline color="grey-7" icon="arrow_back" label="Volver"
                                        @click="$refs.stepper.previous()" class="full-width" />
                                </div>
                                <div class="col">
                                    <q-btn v-if="cartStore.items.length > 0" unelevated color="primary"
                                        :icon="step === 3 ? 'check_circle' : 'arrow_forward'"
                                        :label="step === 3 ? 'Finalizar' : 'Continuar'" @click="nextStep()"
                                        class="full-width" />
                                </div>
                            </div>
                            <div class="row q-gutter-sm q-mt-sm">
                                <div class="col">
                                    <q-btn outline color="orange" icon="delete_sweep" label="Limpiar"
                                        @click="confirmClear()" class="full-width"
                                        :disable="cartStore.items.length === 0" />
                                </div>
                                <div class="col">
                                    <q-btn flat color="grey-7" icon="close" label="Cancelar" @click="cartStore.toggle()"
                                        class="full-width" />
                                </div>
                            </div>
                        </q-stepper-navigation>
                    </template>
                </q-stepper>
            </q-card-section>
        </q-card>
    </q-dialog>
</template>

<script setup>
import PresupuestoService from "@/services/PresupuestoService";
import StepObras from "./CartMenu/StepObras.vue";
import StepPresupuesto from "./CartMenu/StepPresupuesto.vue";
import StepShoppingCart from "./CartMenu/StepShoppingCart.vue";
import { ref, computed } from "vue";
import { useCartStore } from "@/stores/useCartStore";
import { useQuasar } from "quasar";

const cartStore = useCartStore();
const $q = useQuasar();

defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
});
defineEmits(["update:modelValue"]);

const cartTitle = computed(() => {
    if (cartStore.getMode === "edit") return "Edición de presupuesto";
    return "Creación de presupuesto";
});

const step = ref(1);
const stepper = ref();

const nextStep = () => {
    if (step.value === 3) {
        //ultimo paso
        $q.dialog({
            title: cartStore.getMode === "edit" ? "Edición de presupuesto" : "Generación de presupuesto",
            message: cartStore.getMode === "edit"
                ? "¿Confirma la edición del presupuesto?"
                : "¿Confirma la generación del presupuesto?",
            cancel: {
                label: "Cancelar",
                color: "grey-7",
                flat: true,
            },
            ok: {
                label: "Confirmar",
                color: "primary",
                unelevated: true,
            },
            persistent: true,
            class: "modern-dialog",
        }).onOk(() => {
            cartStore.toggle();
            if (cartStore.getMode === "edit") presupuestoUpdate();
            else presupuestoStore();
        });
    } else {
        stepper.value.next();
    }
};

const confirmClear = () => {
    $q.dialog({
        title: "Limpiar Carrito",
        message: "¿Está seguro de eliminar todos los productos del carrito?",
        cancel: {
            label: "Cancelar",
            color: "grey-7",
            flat: true,
        },
        ok: {
            label: "Limpiar",
            color: "orange",
            unelevated: true,
        },
        persistent: true,
    }).onOk(() => {
        cartStore.clear();
    });
};

const presupuestoUpdate = async () => {
    $q.loading.show({
        message: "Guardando presupuesto",
    });
    try {
        const {
            fecha_necesidad,
            tipo_cambio,
            condicion_pago: { value },
            presupuesto_id,
            ubicacion_id,
        } = cartStore.getPresupuesto;
        await PresupuestoService.update(presupuesto_id, {
            obra_id: cartStore.getObra.id,
            fecha_necesidad,
            tipo_cambio,
            condicion_pago_id: value,
            ubicacion_id,
            items: cartStore.getItemsToDB,
        });

    } catch (error) {
        console.log(error);
    } finally {
        $q.loading.hide();
    }
};

const presupuestoStore = async () => {
    $q.loading.show({
        message: "Guardando presupuesto",
    });
    try {
        const { fecha_necesidad, tipo_cambio, condicion_pago, vendedor, acopio, ubicacion_id } =
            cartStore.getPresupuesto;
        const items = cartStore.getItemsToDB;
        await PresupuestoService.store({
            obra_id: cartStore.getObra.id,
            fecha_necesidad,
            tipo_cambio,
            condicion_pago_id: condicion_pago,
            vendedor_id: vendedor.value,
            acopio,
            ubicacion_id,
            items,
        });
        cartStore.clear();
        $q.loading.hide();
    } catch (error) {
        console.log(error)
    } finally {
        $q.loading.hide();
    }
};
</script>

<style lang="scss" scoped>
.cart-menu-card {
    display: flex;
    flex-direction: column;
    height: 100vh;

    // Gradiente para el header
    .bg-gradient-primary {
        background: linear-gradient(135deg, var(--q-primary) 0%, #1565c0 100%);
        flex-shrink: 0;
    }

    .progress-bar-animated {
        position: relative;
        overflow: hidden;

        &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            background: linear-gradient(90deg,
                    transparent,
                    rgba(255, 255, 255, 0.3),
                    transparent);
            animation: shimmer 2s infinite;
        }
    }

    @keyframes shimmer {
        0% {
            transform: translateX(-100%);
        }

        100% {
            transform: translateX(100%);
        }
    }

    .cart-content {
        flex: 1;
        overflow-y: auto;
        padding: 20px;

        &::-webkit-scrollbar {
            width: 8px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
        }

        &::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;

            &:hover {
                background: #555;
            }
        }
    }
}

// Stepper moderno
.modern-stepper {
    :deep(.q-stepper__step-inner) {
        padding: 20px;
    }

    :deep(.q-stepper--vertical > .q-stepper__nav) {
        padding: 24px 24px 20px;
    }

    :deep(.q-stepper__tab) {
        padding: 16px;
        border-radius: 8px;
        transition: all 0.3s ease;

        &:hover {
            background-color: rgba(25, 118, 210, 0.04);
        }
    }

    :deep(.q-stepper__dot) {
        font-size: 20px;
        width: 40px;
        height: 40px;
    }

    :deep(.q-stepper__title) {
        font-size: 1rem;
        font-weight: 600;
        line-height: 1.4;
    }

    :deep(.q-stepper__caption) {
        font-size: 0.85rem;
        color: rgba(0, 0, 0, 0.6);
    }

    :deep(.q-stepper__step--active) {
        .q-stepper__tab {
            background-color: rgba(25, 118, 210, 0.08);
        }
    }

    :deep(.q-stepper__step--done) {
        .q-stepper__tab {
            cursor: pointer;
        }
    }
}

// Navigation moderna
.modern-navigation {
    padding: 16px 0;
    background: white;
    position: sticky;
    bottom: 0;
    z-index: 10;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

// Mejora de tipografía
.text-white-7 {
    color: rgba(255, 255, 255, 0.7);
}

// Animaciones
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

// Responsive
@media (max-width: 599px) {
    .cart-menu-card {
        .cart-content {
            padding: 12px;
        }
    }
}
</style>
